import { http, buildGetUrl } from 'src/utils/request';
import { parseResponse } from './common';

/**
 * 获取未读消息列表
 * @param {Array<string|number>} sessionIds - 会话ID数组
 * @param {Object} config - 配置参数
 * @param {boolean} config.showLoading - 是否显示全局 loading，默认为 true
 * @returns {Promise<Object>} API响应结果
 */
export const getUnreadMessages = async (sessionIds, config = {}) => {
  const { showLoading = true, ...otherConfig } = config;
  const url = '/api/chat/v1/message/list-unread';
  return http
    .post(
      url,
      {
        sessionIds: sessionIds,
      },
      {
        ...otherConfig,
        headers: {
          ...otherConfig.headers,
          'X-Show-Loading': showLoading.toString(),
        },
      }
    )
    .then(response => parseResponse(response))
    .catch(error => {
      console.error('获取未读消息列表失败:', error);
      throw new Error(error.message || '获取未读消息列表失败');
    });
};

/**
 * 重置未读消息
 * @param {string} sessionId - 会话ID
 * @param {Object} config - 配置参数
 * @param {boolean} config.showLoading - 是否显示全局 loading，默认为 false
 * @returns {Promise<Object>} API响应结果
 */
export const resetUnreadMessages = async (sessionId, config = {}) => {
  const { showLoading = false, ...otherConfig } = config;
  const url = '/api/chat/v1/message/reset-unread';
  const response = await http.post(
    url,
    { sessionId },
    {
      ...otherConfig,
      headers: {
        ...otherConfig.headers,
        'X-Show-Loading': showLoading.toString(),
      },
    }
  );
  return parseResponse(response);
};

/**
 * 获取话术分类列表
 */
export const fetchScriptCategories = async () => {
  const url = '/api/chat/v1/script-categories';
  const response = await http.get(url);
  return parseResponse(response);
};

/**
 * 新增话术分类
 * @param {string} name 分类名称
 */
export const createScriptCategory = async name => {
  const url = '/api/chat/v1/script-categories';
  const response = await http.post(url, { name });
  return parseResponse(response);
};

/**
 * 编辑话术分类
 * @param {number} id 分类ID
 * @param {string} name 分类名称
 */
export const updateScriptCategoryName = async (id, name) => {
  const url = `/api/chat/v1/script-categories/${id}`;
  const response = await http.put(url, { name });
  return parseResponse(response);
};

/**
 * 删除话术分类
 * @param {number} id 分类ID
 */
export const deleteScriptCategory = async id => {
  const url = `/api/chat/v1/script-categories/${id}`;
  const response = await http.delete(url);
  return parseResponse(response);
};

/**
 * 获取分类下的话术数量
 * @param {number} id 分类ID
 */
export const getScriptCountByCategory = async id => {
  const url = `/api/chat/v1/script-categories/${id}/script-count`;
  const response = await http.get(url);
  return parseResponse(response);
};

// 获取话术列表（分页、搜索、分类）
export const fetchScriptLibraryList = async ({
  categoryId = -1,
  search = '',
  page = 1,
  pageSize = 20,
}) => {
  const baseUrl = '/api/chat/v1/script-library';
  // 使用 buildGetUrl 来构建 URL，避免 axios 参数序列化问题
  const url = buildGetUrl(baseUrl, {
    categoryId,
    search,
    page,
    pageSize,
  });
  const response = await http.get(url);
  return parseResponse(response);
};

// 新增话术
export const createScriptLibrary = async data => {
  const url = '/api/chat/v1/script-library';
  const response = await http.post(url, data);
  return parseResponse(response);
};

// 编辑话术
export const updateScriptLibrary = async (id, data) => {
  const url = `/api/chat/v1/script-library/${id}`;
  const response = await http.put(url, data);
  return parseResponse(response);
};

// 修改话术排序号
export const updateScriptSortOrder = async (id, sortOrder) => {
  const url = `/api/chat/v1/script-library/${id}`;
  const response = await http.put(url, { sortOrder });
  return parseResponse(response);
};

// 修改话术分类
export const updateScriptCategory = async (id, categoryId) => {
  const url = `/api/chat/v1/script-library/${id}`;
  const response = await http.put(url, { categoryId });
  return parseResponse(response);
};

// 批量更新分类排序
export const updateCategoryOrders = async categoryOrders => {
  const url = '/api/chat/v1/script-categories/batch-update-orders';
  const response = await http.post(url, { categoryOrders });
  return parseResponse(response);
};

// 删除话术
export const deleteScriptLibrary = async id => {
  const url = `/api/chat/v1/script-library/${id}`;
  const response = await http.delete(url);
  return parseResponse(response);
};

// 获取话术详情
export const getScriptLibraryById = async id => {
  const url = `/api/chat/v1/script-library/${id}`;
  const response = await http.get(url);
  return parseResponse(response);
};

/**
 * 批量推送消息
 * @param {Object} params - 推送参数
 * @param {string} params.sessionId - 会话ID
 * @param {Array<Object>} params.messageJsonContent - 消息内容数组（原有格式）
 * @param {string} params.contentJson - 消息内容JSON字符串（新格式，当有 taskId 时使用）
 * @param {number} params.taskId - 任务ID，用于关联批量发送任务
 * @returns {Promise<Object>} API响应结果
 */
export const scriptLibrarySend = async params => {
  const url = '/api/chat/v1/script-library/send';
  const { sessionId, messageJsonContent, contentJson, taskId, messageTag } = params;

  // 构建请求数据
  const requestData = {
    sessionId: sessionId?.toString(),
  };

  // 构建请求头
  const requestHeaders = {};

  // 根据消息格式选择不同的数据格式
  if (contentJson) {
    // 使用 contentJson 格式（JSON字符串）
    // 按照 Swagger 文档约定：使用此格式时必须提供 x-message-tag 头信息
    requestData.contentJson = contentJson;
    if (!messageTag) {
      throw new Error('使用 contentJson 格式时，必须提供 messageTag 参数');
    }
    requestHeaders['x-message-tag'] = messageTag;
  } else if (messageJsonContent) {
    // 使用 messageJsonContent 格式（数组）
    requestData.messageJsonContent = messageJsonContent;
  }

  // taskId 是独立的，如果有则设置（可以与 contentJson 或 messageJsonContent 一起使用）
  if (taskId) {
    requestHeaders['x-task-id'] = taskId.toString();
  }

  return http
    .post(url, requestData, {
      headers: requestHeaders,
    })
    .then(response => parseResponse(response))
    .catch(error => {
      console.error('批量推送消息失败:', error);
      throw new Error(error.message || '批量推送消息失败');
    });
};

/**
 * 批量获取用户会话信息
 * @param {Array<string|number>} userIds - 用户ID数组
 * @returns {Promise<Object>} API响应结果，包含用户信息和会话ID
 */
export const batchGetUserConversations = async userIds => {
  const url = '/api/chat/v1/private/users';
  const requestBody = {
    userIds: userIds.join(','),
  };

  const response = await http.post(url, requestBody);
  return parseResponse(response);
};

/**
 * 发送消息到群组
 * @param {Object} messageData - 消息数据
 * @param {string} messageData.chatUserId - 聊天用户ID
 * @param {string} messageData.fromUserId - 发送者用户ID
 * @param {string} messageData.content - 消息内容
 * @param {string} messageData.msgType - 消息类型，默认'text'
 * @param {Object} config - 配置参数
 * @param {boolean} config.showLoading - 是否显示全局 loading，默认为 false
 * @returns {Promise<Object>} API响应结果
 */
export const sendGroupMessage = async (messageData, config = {}) => {
  const { showLoading = false, ...otherConfig } = config;
  const url = '/api/chat/v1/messages/send';
  const response = await http.post(url, messageData, {
    ...otherConfig,
    headers: {
      ...otherConfig.headers,
      'X-Show-Loading': showLoading.toString(),
    },
  });
  return parseResponse(response);
};
